import React, { useState } from 'react';
import { ArrowLeft, ExternalLink, CreditCard, Smartphone, Globe, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/UI/Card';
import { Button } from '../components/UI/Button';

const providers = [
  {
    id: 'moonpay',
    name: 'MoonPay',
    icon: CreditCard,
    description: 'Buy with credit/debit card',
    fees: '3.5% + network fees',
    limits: '$20 - $50,000',
    available: false,
    supportedMethods: ['Visa', 'Mastercard', 'Apple Pay', 'Google Pay'],
  },
  {
    id: 'banxa',
    name: 'Banxa',
    icon: Smartphone,
    description: 'Bank transfer & card payments',
    fees: '1.5% - 3.9%',
    limits: '$50 - $10,000',
    available: false,
    supportedMethods: ['Bank Transfer', 'Credit Card', 'Debit Card'],
  },
  {
    id: 'ramp',
    name: 'Ramp',
    icon: Globe,
    description: 'Global payment methods',
    fees: '2.9% + network fees',
    limits: '$1 - $20,000',
    available: false,
    supportedMethods: ['Card', 'Bank Transfer', 'Open Banking'],
  },
];

export const Buy: React.FC = () => {
  const navigate = useNavigate();
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);

  const handleProviderSelect = (providerId: string) => {
    setSelectedProvider(providerId);
    // In a real app, this would redirect to the provider's widget or API
    alert('🚧 Coming Soon!\n\nThis provider integration is currently under development. We\'re working hard to bring you seamless ALGO purchasing options.');
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="flex items-center p-4 bg-white border-b border-gray-100">
        <button
          onClick={() => navigate('/')}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors mr-3"
        >
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">Buy ALGO</h1>
      </div>

      <div className="p-4">
        {/* Info Card */}
        <Card className="mb-6 bg-gradient-to-r from-primary-50 to-blue-50 border-primary-200">
          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Purchase ALGO with Fiat Currency
            </h2>
            <p className="text-sm text-gray-600">
              Choose from our trusted payment providers to buy ALGO directly with your credit card, 
              bank transfer, or other payment methods. All transactions are secure and regulated.
            </p>
          </div>
        </Card>

        {/* Current ALGO Price */}
        <Card className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Current ALGO Price</p>
              <p className="text-2xl font-bold text-gray-900">$0.21</p>
              <p className="text-xs text-gray-500">Last updated: Just now</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-green-600 font-medium">+5.2%</p>
              <p className="text-xs text-gray-500">24h change</p>
              <div className="mt-1">
                <p className="text-xs text-gray-500">24h High: $0.22</p>
                <p className="text-xs text-gray-500">24h Low: $0.19</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Development Notice */}
        <Card className="mb-6 bg-yellow-50 border-yellow-200">
          <div className="flex items-start space-x-3">
            <AlertTriangle size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-yellow-800 mb-1">Development Notice</h3>
              <p className="text-sm text-yellow-700">
                Payment provider integrations are currently under development. 
                We're working to bring you the best rates and user experience.
              </p>
            </div>
          </div>
        </Card>

        {/* Payment Providers */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Choose Payment Provider</h3>
          
          {providers.map((provider) => {
            const Icon = provider.icon;
            return (
              <Card 
                key={provider.id} 
                hover={provider.available}
                className={`${!provider.available ? 'opacity-75' : ''}`}
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-primary-600" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{provider.name}</h4>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                        Coming Soon
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3">{provider.description}</p>
                    
                    <div className="space-y-2 mb-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Fees:</span>
                        <span className="text-gray-900 font-medium">{provider.fees}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Limits:</span>
                        <span className="text-gray-900 font-medium">{provider.limits}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Methods:</span>
                        <span className="text-gray-900 font-medium text-right">
                          {provider.supportedMethods.join(', ')}
                        </span>
                      </div>
                    </div>
                    
                    <Button
                      className="w-full"
                      variant={provider.available ? "primary" : "outline"}
                      size="sm"
                      disabled={!provider.available}
                      onClick={() => handleProviderSelect(provider.id)}
                    >
                      {provider.available ? (
                        <>
                          Buy with {provider.name}
                          <ExternalLink size={14} className="ml-1" />
                        </>
                      ) : (
                        'Coming Soon'
                      )}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* How it Works */}
        <Card className="mt-6">
          <h4 className="font-semibold text-gray-900 mb-3">How it Works</h4>
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-start space-x-2">
              <span className="w-5 h-5 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">1</span>
              <p>Choose your preferred payment provider</p>
            </div>
            <div className="flex items-start space-x-2">
              <span className="w-5 h-5 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">2</span>
              <p>Complete identity verification (KYC) if required</p>
            </div>
            <div className="flex items-start space-x-2">
              <span className="w-5 h-5 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">3</span>
              <p>Enter purchase amount and payment details</p>
            </div>
            <div className="flex items-start space-x-2">
              <span className="w-5 h-5 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">4</span>
              <p>ALGO is sent directly to your wallet</p>
            </div>
          </div>
        </Card>

        {/* Disclaimer */}
        <Card className="mt-6 bg-gray-50 border-gray-200">
          <div className="text-center">
            <h4 className="font-medium text-gray-900 mb-2">Important Disclaimer</h4>
            <p className="text-sm text-gray-600">
              Third-party payment providers are independent services with their own terms and conditions. 
              Algowise facilitates the connection but is not responsible for provider services, fees, or policies. 
              Please review each provider's terms before making a purchase.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};